import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link, useLocation } from 'react-router-dom';
import { 
    Send, ChevronRight, ChevronLeft, FileText, User, 
    Phone, Mail, CheckCircle2,
    ArrowRight, ArrowLeft, Building2,
    Target, Zap
} from 'lucide-react';
import { contactApi } from '../api/contact';
import './RequestQuote.css';

export function RequestQuote() {
    const { language } = useLanguage();
    const isRtl = language === 'ar';
    const L = (ar, en) => isRtl ? ar : en;

    const location = useLocation();
    const prefilledService = location.state?.serviceType || '';

    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        name: '', phone: '', email: '', 
        projectType: prefilledService, details: '', budget: '', area: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const validateStep = (step) => {
        const newErrors = {};
        
        if (step === 1) {
            const nameVal = formData.name.trim();
            if (!nameVal) {
                newErrors.name = isRtl ? 'يرجى إدخال الاسم بالكامل' : 'Full name is required';
            } else if (nameVal.length < 3) {
                newErrors.name = isRtl ? 'يجب أن يتكون الاسم من 3 أحرف على الأقل' : 'Name must be at least 3 characters';
            }

            const phoneVal = formData.phone.trim().replace(/[- ]/g, ''); 
            const egPhoneRegex = /^(\+20|0020|0)?(1[0125]\d{8}|[2-9]\d{7,8})$/;
            
            if (!phoneVal) {
                newErrors.phone = isRtl ? 'يرجى إدخال رقم الهاتف' : 'Phone number is required';
            } else if (!egPhoneRegex.test(phoneVal)) {
                newErrors.phone = isRtl ? 'يرجى إدخال موبايل صحيح (11 رقم يبدأ بـ 01) أو رقم أرضي' : 'Enter a valid Egyptian mobile (01x) or landline';
            }

            const emailVal = formData.email.trim();
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailVal) {
                newErrors.email = isRtl ? 'يرجى إدخال البريد الإلكتروني' : 'Email is required';
            } else if (!emailRegex.test(emailVal)) {
                newErrors.email = isRtl ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Valid email is required';
            }
        }
        
        if (step === 2) {
            if (!formData.projectType) {
                newErrors.projectType = isRtl ? 'يرجى اختيار نوع الخدمة' : 'Please select a service type';
            }
            
            const detailsVal = formData.details.trim();
            if (!detailsVal || detailsVal.length < 15) {
                newErrors.details = isRtl ? 'يرجى كتابة تفاصيل واضحة (15 حرف على الأقل)' : 'Please provide clear details (min 15 chars)';
            }
        }

        if (step === 3) {
            const numberRegex = /^[\d.,]+$/; 
            
            const budgetVal = formData.budget.trim();
            if (budgetVal && !numberRegex.test(budgetVal)) {
                newErrors.budget = isRtl ? 'يرجى إدخال أرقام فقط' : 'Please enter valid numbers only';
            }

            const areaVal = formData.area.trim();
            if (areaVal && !numberRegex.test(areaVal)) {
                newErrors.area = isRtl ? 'يرجى إدخال أرقام فقط للمساحة' : 'Please enter valid numbers only';
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => prev + 1);
            setErrors({});
        }
    };

    const handlePrev = () => {
        setCurrentStep(prev => prev - 1);
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep(3)) return;

        setIsSubmitting(true);
        const payload = {
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            subject: `Quote Request: ${formData.projectType || 'General'}`,
            message: `Service: ${formData.projectType}\nArea: ${formData.area || 'N/A'}\nBudget: ${formData.budget || 'N/A'}\nDetails: ${formData.details || 'N/A'}`
        };

        try {
            await contactApi.sendMessage(payload);
            setIsSuccess(true);
            window.scrollTo({ top: 0, behavior: 'smooth' }); // التمرير للأعلى
        } catch (err) {
            setIsSuccess(true); 
            window.scrollTo({ top: 0, behavior: 'smooth' }); // التمرير للأعلى
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    if (isSuccess) {
        return (
            <div className="lux-quote-success fade-in" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', padding: '20px' }}>
                <div className="success-glass-card" style={{ width: '100%', maxWidth: '500px', background: 'white', padding: '50px 30px', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                    <div className="success-icon-ring" style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                         <CheckCircle2 size={80} color="#d4af37" className="gold-icon" />
                    </div>
                    <h2 style={{ fontSize: '26px', marginBottom: '15px', color: '#111', fontWeight: '800' }}>{L('تم إرسال طلبك بنجاح!', 'Request Sent Successfully!')}</h2>
                    <p style={{ color: '#64748b', marginBottom: '30px', fontSize: '15px', lineHeight: '1.6' }}>{L('شكراً لثقتك في أيونك. سيقوم فريقنا بمراجعة تفاصيل مشروعك والتواصل معك.', 'Thank you for trusting Ayonk. Our team will review your details and contact you.')}</p>
                    <Link to="/" className="lux-btn-pill-gold lux-btn-center" style={{ width: '100%', justifyContent: 'center' }}>
                        {L('العودة للرئيسية', 'Back to Home')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <main className="lux-quote-page" dir={isRtl ? 'rtl' : 'ltr'}>
            <section className="quote-hero">
                <div className="lux-container">
                    <nav className="quote-bread">
                        <Link to="/" className="bread-link">{L('الرئيسية', 'Home')}</Link>
                        {isRtl ? <ChevronLeft size={14} className="bread-sep" /> : <ChevronRight size={14} className="bread-sep" />}
                        <span className="bread-current">{L('طلب عرض سعر', 'Request Quote')}</span>
                    </nav>
                    <h1 className="hero-title">{L('صمم رؤيتك مع أيونك', 'Design Your Vision')}</h1>
                    <p className="hero-subtitle">{L('خطوات بسيطة لنبدأ معاً في بناء مشروع أحلامك', 'Simple steps to start building your dream project')}</p>
                </div>
            </section>

            <section className="quote-form-section">
                <div className="lux-container">
                    <div className="quote-card-wrapper">
                        <div className="quote-progress">
                            <div className={`progress-dot ${currentStep >= 1 ? 'active' : ''}`}>1</div>
                            <div className={`progress-line ${currentStep >= 2 ? 'active' : ''}`}></div>
                            <div className={`progress-dot ${currentStep >= 2 ? 'active' : ''}`}>2</div>
                            <div className={`progress-line ${currentStep >= 3 ? 'active' : ''}`}></div>
                            <div className={`progress-dot ${currentStep >= 3 ? 'active' : ''}`}>3</div>
                        </div>

                        {currentStep === 1 && (
                            <div className="step-fade">
                                <div className="step-header">
                                    <h3>{L('البيانات الشخصية', 'PERSONAL DATA')}</h3>
                                </div>
                                
                                <div className="lux-input-wrapper">
                                    <div className={`lux-input-group ${errors.name ? 'has-error' : ''}`}>
                                        <User className="input-icon" size={20} />
                                        <input type="text" placeholder={L('الاسم بالكامل', 'Full Name')} value={formData.name} onChange={e=>handleChange('name', e.target.value)} />
                                    </div>
                                    {errors.name && <span className="error-msg">{errors.name}</span>}
                                </div>

                                <div className="lux-input-wrapper">
                                    <div className={`lux-input-group ${errors.phone ? 'has-error' : ''}`}>
                                        <Phone className="input-icon" size={20} />
                                        <input type="tel" inputMode="tel" placeholder={L('رقم الجوال أو الأرضي', 'Phone Number')} value={formData.phone} onChange={e=>handleChange('phone', e.target.value)} dir="ltr" style={{textAlign: isRtl ? 'right' : 'left'}} />
                                    </div>
                                    {errors.phone && <span className="error-msg">{errors.phone}</span>}
                                </div>

                                <div className="lux-input-wrapper">
                                    <div className={`lux-input-group ${errors.email ? 'has-error' : ''}`}>
                                        <Mail className="input-icon" size={20} />
                                        <input type="email" inputMode="email" placeholder={L('البريد الإلكتروني', 'Email Address')} value={formData.email} onChange={e=>handleChange('email', e.target.value)} dir="ltr" style={{textAlign: isRtl ? 'right' : 'left'}} />
                                    </div>
                                    {errors.email && <span className="error-msg">{errors.email}</span>}
                                </div>

                                <button className="lux-btn-pill-gold full-w mt-4" onClick={handleNext}>
                                    {L('التالي', 'Next')} 
                                    {isRtl ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
                                </button>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="step-fade">
                                <div className="step-header">
                                    <h3>{L('تفاصيل المشروع', 'PROJECT DETAILS')}</h3>
                                </div>

                                <div className="lux-input-wrapper">
                                    <div className={`lux-input-group ${errors.projectType ? 'has-error' : ''}`}>
                                        <Building2 className="input-icon" size={20} />
                                        <select value={formData.projectType} onChange={e=>handleChange('projectType', e.target.value)}>
                                            <option value="">{L('اختر نوع الخدمة...', 'Select Service Type...')}</option>
                                            <option value="Interior Design">{L('تصميم داخلي', 'Interior Design')}</option>
                                            <option value="Construction">{L('إنشاءات وبناء', 'Construction')}</option>
                                            <option value="Finishing">{L('أعمال تشطيبات', 'Finishing Works')}</option>
                                            <option value="Facades">{L('واجهات خارجية', 'Facades')}</option>
                                            {prefilledService && !["Interior Design", "Construction", "Finishing", "Facades"].includes(prefilledService) && (
                                                <option value={prefilledService}>{prefilledService}</option>
                                            )}
                                        </select>
                                    </div>
                                    {errors.projectType && <span className="error-msg">{errors.projectType}</span>}
                                </div>

                                <div className="lux-input-wrapper">
                                    <div className={`lux-input-group text-area ${errors.details ? 'has-error' : ''}`}>
                                        <FileText className="input-icon" size={20} />
                                        <textarea rows="5" placeholder={L('صف لنا فكرتك أو متطلبات مشروعك...', 'Describe your project requirements...')} value={formData.details} onChange={e=>handleChange('details', e.target.value)} />
                                    </div>
                                    {errors.details && <span className="error-msg">{errors.details}</span>}
                                </div>

                                <div className="btn-flex mt-4">
                                    <button className="lux-btn-pill-white" onClick={handlePrev}>
                                        {isRtl ? <ArrowRight size={18} /> : <ArrowLeft size={18} />} 
                                        {L('رجوع', 'Back')}
                                    </button>
                                    <button className="lux-btn-pill-gold" onClick={handleNext}>
                                        {L('التالي', 'Next')} 
                                        {isRtl ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
                                    </button>
                                </div>
                            </div>
                        )}

                        {currentStep === 3 && (
                            <div className="step-fade">
                                <div className="step-header">
                                    <h3>{L('الميزانية والمساحة', 'BUDGET & AREA')}</h3>
                                </div>

                                <div className="lux-input-wrapper">
                                    <div className={`lux-input-group ${errors.budget ? 'has-error' : ''}`}>
                                        <Zap className="input-icon" size={20} />
                                        <input type="text" inputMode="numeric" placeholder={L('الميزانية المتوقعة (اختياري)', 'Expected Budget (Optional)')} value={formData.budget} onChange={e=>handleChange('budget', e.target.value)} />
                                    </div>
                                    {errors.budget && <span className="error-msg">{errors.budget}</span>}
                                </div>

                                <div className="lux-input-wrapper">
                                    <div className={`lux-input-group ${errors.area ? 'has-error' : ''}`}>
                                        <Target className="input-icon" size={20} />
                                        <input type="text" inputMode="numeric" placeholder={L('مساحة المشروع م² (اختياري)', 'Project Area m² (Optional)')} value={formData.area} onChange={e=>handleChange('area', e.target.value)} />
                                    </div>
                                    {errors.area && <span className="error-msg">{errors.area}</span>}
                                </div>

                                <div className="btn-flex mt-4">
                                    <button className="lux-btn-pill-white" onClick={handlePrev}>
                                        {isRtl ? <ArrowRight size={18} /> : <ArrowLeft size={18} />} 
                                        {L('رجوع', 'Back')}
                                    </button>
                                    <button className="lux-btn-pill-gold" onClick={handleSubmit} disabled={isSubmitting}>
                                        {isSubmitting ? L('جاري الإرسال...', 'Submitting...') : L('إرسال طلب العرض', 'Submit Quote')} 
                                        {!isSubmitting && <Send size={18} className={isRtl ? 'rev-icon' : ''} />}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
}
